import { useMemo } from "react";
import Badge from "react-bootstrap/Badge";
import { useDispatch } from "react-redux";
import { resources } from "@constants/hardcoded-strings";
import { ENTITY_STATUS_OPTIONS } from "@constants/entity-status-options";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { useTableFilters } from "@hooks/use-table-filters";
import { createColumnHelper, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, TableState, useReactTable } from "@tanstack/react-table";
import { sortStrings } from "@utils/string-util";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { IdwTable } from "@containers/common/idw-table/idw-table";
import { PaginationContainer } from "@containers/common/pagination-container/pagination-container";
import { IdwTableContainer } from "@containers/common/idw-table-container/idw-table-container";
import { handleSetColumnFiltersChange, handleSetPaginationChange, handleSetSortingChange } from "@utils/react-table/react-table-util";
import styles from "./email-templates-table-container.less";
import { EmailTemplatesCell } from "@containers/email-templates/email-templates-table-cell/email-templates-table-cell";
import { FormattedEmailTemplate } from "@idw-types/data/email-template";

type EmailTemplatesTableContainerProps = {
    emailTemplates: Array<FormattedEmailTemplate>;
    tableState: TableState;
    isPageHeight: boolean;
};

type EmailTemplatesTableDataRow = FormattedEmailTemplate & {
    companyName: string;
};

export const EmailTemplatesTableContainer = (
    props: EmailTemplatesTableContainerProps
) => {
    const dispatch = useDispatch();

    const { emailTemplates, tableState, isPageHeight } = props;

    const emailTemplateTableData: Array<EmailTemplatesTableDataRow> = useMemo(() => {
        return emailTemplates.map(template => ({
            ...template,
            companyName: template.company?.name || "IDDataweb (Public)"
        }));
    }, [emailTemplates]);

    const columnHelper = createColumnHelper<EmailTemplatesTableDataRow>();
    const columns = useMemo(() => ([
        columnHelper.accessor("name", {
            header: resources.tables.emailTemplates.columns.name.header,
            filterFn: "fuzzyText",
            cell: (props) => (
                <EmailTemplatesCell
                    value={props.getValue()}
                    emailTemplate={props.row.original}
                />
            ),
            sortingFn: (a, b) => sortStrings(a.original.name, b.original.name)
        }),
        columnHelper.accessor("companyName", {
            header: resources.tables.emailTemplates.columns.organization.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.companyName, b.original.companyName)
        }),
        columnHelper.accessor("status", {
            header: resources.common.labels.status,
            filterFn: "singleSelectFilter",
            meta: {
                filterOptions: ENTITY_STATUS_OPTIONS
            },
            cell: (props) => {
                switch (props.getValue()) {
                    case EntityStatusTypes.ACTIVE:
                        return <Badge bg={"success"}>{resources.common.active}</Badge>;
                    case EntityStatusTypes.INACTIVE:
                        return <Badge bg={"danger"}>{resources.common.inactive}</Badge>;
                    default:
                        return <span>{resources.common.notFound}</span>;
                }                
            }
        })
    ]), [emailTemplateTableData]);

    const { filterTypes } = useTableFilters();

    const useTableResult = useReactTable({
        columns,
        data: emailTemplateTableData,
        state: tableState,
        filterFns: filterTypes,
        autoResetPageIndex: true,
        onSortingChange: (sortingOrUpdateFn) => handleSetSortingChange(sortingOrUpdateFn, TableSettingsTableTypes.EMAIL_TEMPLATES, tableState.sorting, dispatch),
        onColumnFiltersChange: (filtersOrUpdateFn) => handleSetColumnFiltersChange(filtersOrUpdateFn, TableSettingsTableTypes.EMAIL_TEMPLATES, tableState.columnFilters, dispatch),
        onPaginationChange: (paginationOrUpdateFn) => handleSetPaginationChange(paginationOrUpdateFn, TableSettingsTableTypes.EMAIL_TEMPLATES, tableState.pagination, dispatch),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    return (
        <IdwTableContainer className={styles.emailTemplatesTableContainer}>
            <IdwTable
                id="email-templates"
                useTableResult={useTableResult}
                headerColumnKeyPrefix={"email-templates"}
                className={styles.emailTemplatesTable}
            />
            <PaginationContainer
                idPrefix="email-templates"
                useTableResult={useTableResult}
                isPageHeight={isPageHeight}
            />
        </IdwTableContainer>
    );
};